import { useEffect, useState, type FormEvent } from "react";

import { useSlinkAgents } from "@/api/agents";
import { errorDetail } from "@/components/QueryState";
import {
  useDispatchDiscovery,
  useDiscoveryResults,
} from "@/api/cameras";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

interface DiscoveryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DiscoveryModal({ open, onOpenChange }: DiscoveryModalProps) {
  const [agentId, setAgentId] = useState("");
  const [taskId, setTaskId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatchDiscovery();
  // `agents === undefined` was doing double duty for "still loading" AND
  // "the request failed" — on a 500 the dropdown said "Loading agents…" forever.
  // A <QueryState> wrapper is wrong here: this is one <option> label inside a
  // dialog that also drives the discovery run, so the three states are rendered
  // inline instead. isError is checked FIRST for the same reason QueryState
  // checks it first.
  const agentsQ = useSlinkAgents();
  const agents = agentsQ.data;
  const { data: results } = useDiscoveryResults(taskId);

  useEffect(() => {
    if (!open) {
      setTaskId(null);
      setError(null);
    }
  }, [open]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    try {
      const res = await dispatch.mutateAsync(agentId);
      setTaskId(res.task_id);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Could not start discovery";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  }

  const status = results?.status;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Discover cameras on LAN</DialogTitle>
          <DialogDescription>
            The slink-agent at the target branch will run an ONVIF
            WS-Discovery sweep on its LAN interface. Results stream back via
            Redis.
          </DialogDescription>
        </DialogHeader>

        {!taskId && (
          <form className="space-y-4" onSubmit={onSubmit}>
            <div className="space-y-2">
              <Label htmlFor="agent_id">SLink agent (where the scan runs)</Label>
              <select
                id="agent_id"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                required
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="" disabled>
                  {agentsQ.isError
                    ? "Could not load agents — retry"
                    : agents === undefined
                      ? "Loading agents…"
                      : agents.length === 0
                        ? "No SLink agents enrolled"
                        : "Select an agent…"}
                </option>
                {(agents ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.status && a.status !== "active" ? ` · ${a.status}` : ""}
                  </option>
                ))}
              </select>
              {agentsQ.isError && (
                <p className="mt-1 text-xs text-destructive" data-testid="agents-load-error">
                  Could not load SLink agents{errorDetail(agentsQ.error) ? ` — ${errorDetail(agentsQ.error)}` : ""}.{" "}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => void agentsQ.refetch()}
                  >
                    Retry
                  </button>
                </p>
              )}
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={dispatch.isPending}>
                {dispatch.isPending ? "Starting…" : "Start discovery"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {taskId && (
          <div className="space-y-3">
            <p className="text-sm">
              Task <span className="font-mono">{taskId}</span> ·{" "}
              <Badge
                variant={
                  status === "completed"
                    ? "online"
                    : status === "failed" || status === "expired"
                      ? "error"
                      : "warning"
                }
              >
                {status ?? "pending"}
              </Badge>
            </p>

            {results?.error && (
              <p className="text-sm text-destructive">{results.error}</p>
            )}

            {results && results.cameras.length === 0 && status === "completed" && (
              <p className="text-sm text-muted-foreground">
                Sweep completed; no devices answered.
              </p>
            )}

            {results && results.cameras.length > 0 && (
              <ul className="space-y-2">
                {results.cameras.map((c) => (
                  <li
                    key={`${c.onvif_host}:${c.onvif_port}`}
                    className="rounded border p-3 text-sm"
                  >
                    <div className="flex justify-between">
                      <span className="font-medium">
                        {c.manufacturer ?? "ONVIF device"}{" "}
                        <span className="text-muted-foreground">
                          {c.model ?? ""}
                        </span>
                      </span>
                      <span className="font-mono text-xs">
                        {c.onvif_host}:{c.onvif_port}
                      </span>
                    </div>
                    {c.firmware && (
                      <div className="text-xs text-muted-foreground">
                        firmware {c.firmware}
                      </div>
                    )}
                    {c.serial_number && (
                      <div className="text-xs text-muted-foreground">
                        sn {c.serial_number}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setTaskId(null)}>
                New sweep
              </Button>
              <Button onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
